import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import { GET_COMPANIES } from '../../graphql/companies'
import {
  DELETE_COMPANY_BUSINESS_GROUP,
  GET_COMPANY_BUSINESS_GROUP,
  SET_COMPANY_BUSINESS_GROUP_MEMBERS,
  UPDATE_COMPANY_BUSINESS_GROUP,
} from '../../graphql/companyGroups'

function DeleteModal({ groupName, onClose, onConfirm, saving }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Eliminar grupo</h3>
          <button type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, color: 'var(--scfo-text)' }}>
            ¿Eliminar el grupo <strong>{groupName}</strong>? Las empresas
            asociadas quedarán sin grupo empresarial.
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={onConfirm}
            disabled={saving}
          >
            {saving ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function CompanyGroupDetail({ groupId, onBack }) {
  const history = useHistory()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [companySearch, setCompanySearch] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [savingMeta, setSavingMeta] = useState(false)
  const [savingMembers, setSavingMembers] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [onlySelected, setOnlySelected] = useState(true) // default: solo del grupo

  const [groupResult, reexecuteGroup] = useQuery({
    query: GET_COMPANY_BUSINESS_GROUP,
    variables: { id: groupId },
    requestPolicy: 'network-only',
    pause: !groupId,
  })

  const [companiesResult] = useQuery({
    query: GET_COMPANIES,
    variables: {
      filter: {
        search: companySearch.trim() || undefined,
        page: 1,
        pageSize: 100,
      },
    },
    requestPolicy: 'network-only',
  })

  const [, updateGroup] = useMutation(UPDATE_COMPANY_BUSINESS_GROUP)
  const [, setMembers] = useMutation(SET_COMPANY_BUSINESS_GROUP_MEMBERS)
  const [, deleteGroup] = useMutation(DELETE_COMPANY_BUSINESS_GROUP)

  const group = groupResult.data?.companyBusinessGroup

  useEffect(() => {
    if (!group || initialized) return
    setName(group.name || '')
    setDescription(group.description || '')
    setSelectedIds(
      new Set((group.companies || []).map((c) => String(c.id))),
    )
    setInitialized(true)
  }, [group, initialized])

  useEffect(() => {
    setInitialized(false)
    setError(null)
    setSuccess(null)
    setOnlySelected(true)
  }, [groupId])

  const companies = useMemo(() => {
    const fromSearch = companiesResult.data?.companies?.items || []
    const members = group?.companies || []
    const byId = new Map()

    for (const company of members) {
      const id = String(company.id)
      byId.set(id, {
        _id: id,
        name: company.name,
        rut: company.rut,
        alias: company.alias,
        isMember: true,
      })
    }
    for (const company of fromSearch) {
      const id = String(company._id)
      const prev = byId.get(id)
      byId.set(id, {
        ...company,
        _id: id,
        isMember: prev?.isMember || selectedIds.has(id),
      })
    }

    // IDs seleccionados que ya no aparecen en members ni en la búsqueda
    for (const id of selectedIds) {
      if (!byId.has(id)) {
        byId.set(id, {
          _id: id,
          name: 'Empresa no encontrada',
          rut: id,
          alias: 'Puede haber sido eliminada',
          missing: true,
          isMember: true,
        })
      }
    }

    const list = [...byId.values()]
    list.sort((a, b) => {
      const aSel = selectedIds.has(String(a._id)) ? 0 : 1
      const bSel = selectedIds.has(String(b._id)) ? 0 : 1
      if (aSel !== bSel) return aSel - bSel
      return String(a.name || '').localeCompare(String(b.name || ''), 'es')
    })

    if (onlySelected) {
      return list.filter((c) => selectedIds.has(String(c._id)))
    }
    return list
  }, [companiesResult.data, group, selectedIds, onlySelected])

  const selectedInView = useMemo(
    () => companies.filter((c) => selectedIds.has(String(c._id))).length,
    [companies, selectedIds],
  )

  const handleSaveMeta = async () => {
    const trimmed = name.trim()
    const nextDescription = description.trim()
    if (!trimmed) {
      setError('El nombre es requerido')
      setSuccess(null)
      return
    }
    setSavingMeta(true)
    setError(null)
    setSuccess(null)
    const res = await updateGroup({
      input: {
        id: groupId,
        name: trimmed,
        description: nextDescription,
      },
    })
    setSavingMeta(false)
    if (res.error) {
      setError(res.error.message || 'No se pudo guardar el grupo')
      return
    }

    const updated = res.data?.updateCompanyBusinessGroup
    setName(String(updated?.name ?? trimmed).trim() || trimmed)
    setDescription(
      updated?.description != null
        ? String(updated.description)
        : nextDescription,
    )
    setSuccess('Datos del grupo actualizados')
    // Refrescar query en segundo plano sin pisar el formulario con data stale
    reexecuteGroup({ requestPolicy: 'network-only' })
  }

  const handleSaveMembers = async () => {
    setSavingMembers(true)
    setError(null)
    setSuccess(null)
    const res = await setMembers({
      input: {
        id: groupId,
        companyIds: [...selectedIds],
      },
    })
    setSavingMembers(false)
    if (res.error) {
      setError(res.error.message || 'No se pudo guardar las empresas')
      return
    }

    const updated = res.data?.setCompanyBusinessGroupMembers
    if (updated?.companies) {
      setSelectedIds(new Set(updated.companies.map((c) => String(c.id))))
    }
    setSuccess('Empresas del grupo actualizadas')
    reexecuteGroup({ requestPolicy: 'network-only' })
  }

  const handleDelete = async () => {
    setSavingMeta(true)
    setError(null)
    const res = await deleteGroup({ id: groupId })
    setSavingMeta(false)
    if (res.error) {
      setError(res.error.message || 'No se pudo eliminar el grupo')
      setShowDelete(false)
      return
    }
    history.push('/empresas/grupos')
  }

  const toggleCompany = (id) => {
    const key = String(id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (groupResult.fetching && !group) {
    return (
      <div className="loading-spinner">
        <div className="spinner" />
      </div>
    )
  }

  if (groupResult.error || !group) {
    return (
      <>
        <div className="detail-header">
          <div className="detail-title-section">
            <button type="button" className="back-btn" onClick={onBack}>
              ←
            </button>
            <div>
              <h1 className="page-title">Grupo no encontrado</h1>
            </div>
          </div>
        </div>
        <div className="content-card">
          <div className="card-body">
            <div className="empty-state">
              <p>{groupResult.error?.message || 'Grupo no encontrado'}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="detail-header">
        <div className="detail-title-section">
          <button type="button" className="back-btn" onClick={onBack}>
            ←
          </button>
          <div>
            <h1 className="page-title">{name || group.name}</h1>
            <p className="page-subtitle">
              {selectedIds.size || group.companiesCount || 0} empresa
              {(selectedIds.size || group.companiesCount || 0) === 1 ? '' : 's'} en el grupo
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="action-btn secondary small"
            onClick={() => setShowDelete(true)}
          >
            Eliminar grupo
          </button>
        </div>
      </div>

      {(error || success) && (
        <div
          className="content-card"
          style={{
            marginBottom: '1rem',
            borderColor: error ? '#fecaca' : '#a7f3d0',
          }}
        >
          <div className="card-body" style={{ padding: '0.85rem 1.25rem' }}>
            <p
              style={{
                margin: 0,
                color: error ? '#991b1b' : '#065f46',
                fontSize: '0.9rem',
              }}
            >
              {error || success}
            </p>
          </div>
        </div>
      )}

      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Datos del grupo</h3>
          <button
            type="button"
            className="action-btn primary"
            onClick={handleSaveMeta}
            disabled={savingMeta}
          >
            {savingMeta ? 'Guardando...' : 'Guardar datos'}
          </button>
        </div>
        <div className="card-body">
          <div className="company-group-meta-fields">
            <div className="form-group">
              <label className="form-label" htmlFor="group-name">
                Nombre
              </label>
              <input
                id="group-name"
                className="form-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label" htmlFor="group-description">
                Descripción
              </label>
              <textarea
                id="group-description"
                className="form-input form-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Empresas del grupo</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span className="bulk-count">
              {selectedIds.size} en el grupo
              {!onlySelected && selectedInView !== selectedIds.size
                ? ` · ${selectedInView} en esta vista`
                : ''}
            </span>
            <button
              type="button"
              className="action-btn primary"
              onClick={handleSaveMembers}
              disabled={savingMembers}
            >
              {savingMembers ? 'Guardando...' : 'Guardar empresas'}
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="filters-container" style={{ alignItems: 'flex-end' }}>
            <div className="filter-group" style={{ flex: 1 }}>
              <label className="filter-label">BUSCAR EMPRESAS</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Nombre, RUT o alias..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">VISTA</label>
              <div className="mode-tabs company-group-view-tabs">
                <button
                  type="button"
                  className={`mode-tab ${onlySelected ? 'active' : ''}`}
                  onClick={() => setOnlySelected(true)}
                >
                  Solo del grupo
                </button>
                <button
                  type="button"
                  className={`mode-tab ${!onlySelected ? 'active' : ''}`}
                  onClick={() => setOnlySelected(false)}
                >
                  Ver todas
                </button>
              </div>
            </div>
          </div>

          <div className="companies-list">
            {companiesResult.fetching && companies.length === 0 ? (
              <div className="empty-companies">Cargando empresas...</div>
            ) : companies.length === 0 ? (
              <div className="empty-companies">
                {onlySelected
                  ? 'Este grupo no tiene empresas asignadas'
                  : 'No se encontraron empresas'}
              </div>
            ) : (
              companies.map((company) => {
                const id = String(company._id || company.id)
                const checked = selectedIds.has(id)
                return (
                  <label
                    key={id}
                    className={`company-item ${checked ? 'selected' : ''} ${
                      company.missing ? 'company-item--missing' : ''
                    }`}
                    style={
                      company.missing
                        ? { opacity: 0.75, background: '#fef2f2' }
                        : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCompany(id)}
                    />
                    <span className="company-info">
                      <span className="company-name">
                        {company.name || 'Sin nombre'}
                        {company.missing ? ' (huérfana)' : ''}
                      </span>
                      <span className="company-rut">
                        {company.rut || 'Sin RUT'}
                        {company.alias ? ` · ${company.alias}` : ''}
                      </span>
                    </span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      </div>

      {showDelete ? (
        <DeleteModal
          groupName={group.name}
          onClose={() => setShowDelete(false)}
          onConfirm={handleDelete}
          saving={savingMeta}
        />
      ) : null}
    </>
  )
}

export default CompanyGroupDetail
